import { create } from 'zustand';
import ROUTES from '../data/heli_routes.json';
import NARRATION from '../data/heli_flights.json';
import { buildTrack, sampleTrack, speakingPlan } from './heliRoute';
import { baseGroundY } from './cityGround';
import { IS_MOBILE } from '../config';
import { api } from '../services/api';

// HYPETOWN HELI TOURS (2026-11-01). Khari: "I want to add a helicopter tour
// around the city … Find or create an area in the world for 4 helicopter pads in
// one area. Users will be able to board with friends (I believe 5 can fit + 1
// driver). I will make an npc for the pilot. The tour should be about 90
// seconds long and give a narration of the buildings and events. Record 3
// flights." — and, of where: "place it here in this top left corner" (the
// empty lot at x −120…−81, z −50…−11, read off his plan view).
//
//   THE HELIPORT and THE FLIGHTS are data (data/heli_routes.json): four pads in
//     a 2×2, a booth, the pilot's spot; three routes over named places.
//   A FLIGHT is a function of time (city/heliRoute): the server stamps when a
//     pad's helicopter lifts off (`departAt`), and everyone — aboard, or
//     watching from the street — samples the same track at now − departAt, the
//     times kept on this browser's clock (heliNow).
//   SEATS are the server's (API sockets heliTourStore: five a helicopter, the
//     first aboard picks the flight and takes off). With no server to ask, a
//     flight is this browser's alone (`offline`).
//   A PILOT PER PAD (2026-11-02; Khari: "I will make 4 total; one for each
//     helipad"): a cast member whose home is heliport:pad-N (Web /admin/cast),
//     read live from the API (usePilots, asked every minute) — assigning or swapping one needs no
//     World deploy. They stand by their pad while it is down (Heliport) and fly
//     it (HeliPassengers). Each speaks their own lines (data/heli_flights.json
//     `pilots[avatarId]`, written by the API's scripts/write_heli_tour.js
//     through the chat brain and recorded in their voice) — keyed by PILOT, so
//     a pilot moved to another pad keeps their voice. A pad with no pilot, or a
//     pilot with nothing recorded, still flies: the places named on screen.
export const HELIPORT = ROUTES.heliport;
export const FLIGHTS = ROUTES.flights;
export const NARRATION_V = NARRATION?.v || 0;

/** Who flies each pad: byPad[padId] = { avatarId, name } (loadPilots keeps it current). */
export const usePilots = create(() => ({ byPad: {}, loaded: false }));
/* ⚠ ASKED AGAIN, NOT ONCE (2026-11-02). It was asked once a page load, so an
   answer that failed — the API restarting, or down for a minute — left every
   helicopter pilotless until a reload (Khari: "I can no longer see the pilot in
   her seat"), and a pilot assigned or moved in /admin/cast never arrived either.
   Now: again every minute (the API caches it a minute), sooner after a failure,
   and a failed ask keeps the pilots it already had. */
const PILOTS_EVERY_MS = 60000;
const PILOTS_RETRY_MS = 10000;
let pilotsTimer = null;
export function loadPilots() {
  if (pilotsTimer) return;
  const ask = () => {
    api.heliPilots()
      .then((byPad) => {
        const next = byPad && typeof byPad === 'object' ? byPad : {};
        const st = usePilots.getState();
        if (!st.loaded || JSON.stringify(next) !== JSON.stringify(st.byPad)) usePilots.setState({ byPad: next, loaded: true });
        pilotsTimer = setTimeout(ask, PILOTS_EVERY_MS);
      })
      .catch(() => {   // an API without the route, or not answering: keep what we had, ask again soon
        if (!usePilots.getState().loaded) usePilots.setState({ loaded: true });
        pilotsTimer = setTimeout(ask, PILOTS_RETRY_MS);
      });
  };
  pilotsTimer = setTimeout(ask, 0);
}
export const pilotFor = (padId, st = usePilots.getState()) => {
  const p = st.byPad?.[padId];
  return p && Number(p.avatarId) ? { avatarId: Number(p.avatarId), name: p.name || null } : null;
};
/** A pilot's recorded flights (the single-pilot file of 2026-11-01 is read as that pilot's). */
const recordingsOf = (avatarId) => (avatarId && (NARRATION?.pilots?.[avatarId]?.flights
  || (Number(NARRATION?.pilot?.avatarId) === avatarId ? NARRATION?.flights : null))) || null;

// The model (public/models/cars, git-ignored like every GLB: it ships from the
// local build) — an optimised build of the download (scripts/optimize_heli.mjs):
// 30k triangles and 1024² textures on a computer (you sit in its cabin),
// 14k and 512² on a phone; KTX2/ETC1S textures, meshopt geometry. `?v=` — bump it
// whenever the builds are re-made (served immutable).
export const HELI_MODEL = IS_MOBILE ? '/models/cars/helicopter_mobile.glb?v=20261101b' : '/models/cars/helicopter_desktop.glb?v=20261101b';
export const HELI_LENGTH = 5.8;    // nose to tail, world units (≈12.7m: a six-seat helicopter)
export const SEATS = 5;            // passengers (the pilot is the sixth)
export const BOARD_NEAR = 4.4;     // this close to a pad's centre, you can board its helicopter
export const COUNTDOWN_MS = 4000;  // "take off" → wheels up (the server's, mirrored offline)

/**
 * What the server says of each pad, times on THIS browser's clock:
 *   pads[padId] = { state: 'idle' | 'boarding' | 'flying', flight, departAt,
 *                   endsAt, host, seats: [{ id, handle }] }
 */
export const useHeliTour = create(() => ({
  pads: {},
  mine: null,       // { pad } — you are aboard that pad's helicopter
  near: null,       // the pad you are standing at (Heliport), or null
  view: 'chase',    // the flight's camera: 'chase' (behind it) | 'cabin' (a window seat)
  caption: null,    // { text, kind, place } — what the pilot is saying, or the place below
  offline: false,   // no server answered: your flights are your own
  myId: null,       // your socket id (the server's name for you in `seats`)
}));

/* ⚠ ONE FLIGHT CLOCK, READ ONCE A FRAME (2026-11-02). Khari: "there is a stutter
   of the helicopter and the pilot while I am viewing from within".
   • THE RIGHT CLOCK. The pads' times are kept on THIS browser's clock (worldSocket
     toLocal; an offline flight stamps Date.now()), but the flight was sampled on
     serverNow() — another clock, off by the server offset and re-set by every
     `world_state` (each 10 s): every re-set jumped the helicopter along its track
     by however much the network delay had moved.
   • ONE READING. The camera (HeliDirector) and the helicopter (Helicopter, via
     Heliport) each read the clock in their own useFrame, a millisecond or two
     apart, so the helicopter was drawn a few cm from where the camera had been put
     in it — the seat and the pilot beside you twitched, frame to frame.
   HeliDirector stamps the time first thing each frame (an early useFrame,
   stampHeliFrame); everything flying reads that stamp. */
let frameNow = 0; let frameAt = 0;
export function stampHeliFrame() { frameNow = Date.now(); frameAt = performance.now(); }
/** The flight clock for this frame (the stamp; the plain clock if nothing has stamped lately). */
export function heliNow() { return performance.now() - frameAt < 250 ? frameNow : Date.now(); }

/** Per-frame facts for things that are not React (PlayerBody, Peers, the camera). */
export const heliBridge = { aboard: false, pose: null };

export const flightById = (id) => FLIGHTS.find((f) => f.id === id) || null;
export const padById = (id) => HELIPORT.pads.find((p) => p.id === id) || null;
export const padNumber = (id) => HELIPORT.pads.findIndex((p) => p.id === id) + 1;

const tracks = new Map();
/** A pad's flight as a track (memoised: a flight from a pad never changes). */
export function trackFor(flightId, padId) {
  const key = `${flightId}@${padId}`;
  if (!tracks.has(key)) {
    const f = flightById(flightId); const p = padById(padId);
    tracks.set(key, f && p ? buildTrack(f, p) : null);
  }
  return tracks.get(key);
}

const plans = new Map();
/** When the pilot speaks on a flight (city/heliRoute speakingPlan), with `pilotId`'s recorded line for each slot when there is one. */
export function planFor(flightId, padId, pilotId = null) {
  const key = `${flightId}@${padId}#${pilotId || ''}`;
  if (!plans.has(key)) {
    const f = flightById(flightId); const tr = trackFor(flightId, padId);
    if (!f || !tr) return [];
    const recorded = recordingsOf(pilotId)?.[flightId]?.segments || [];
    plans.set(key, speakingPlan(f, tr).map((slot, i) => ({ ...slot, line: recorded[i] && recorded[i].kind === slot.kind && (recorded[i].cue || null) === (slot.cue || null) ? recorded[i] : null })));
  }
  return plans.get(key);
}

/**
 * Where a pad's helicopter is at local time `nowMs`: { x, y, z, yaw, pitch,
 * roll, spin, phase } — parked on the pad unless it is flying (spin: the rotors,
 * 0 still … 1 flying; they wind up through the countdown).
 */
export function padPose(padId, nowMs, st = useHeliTour.getState()) {
  const pad = padById(padId);
  if (!pad) return null;
  // The track is flown over y=0; the lot's ground is a few cm up (city/cityGround).
  const gy = baseGroundY(pad.x, pad.z);
  const s = st.pads[padId];
  if (s?.state === 'flying' && s.flight && s.departAt) {
    const tr = trackFor(s.flight, padId);
    if (tr) {
      const t = (nowMs - s.departAt) / 1000;
      const p = sampleTrack(tr, t);
      const spin = t < 0 ? Math.max(0.25, 1 + t / (COUNTDOWN_MS / 1000) * 0.75) : t > tr.seconds ? Math.max(0, 1 - (t - tr.seconds) / 4) : 1;
      return { ...p, y: p.y + gy, spin };
    }
  }
  return { x: pad.x, y: gy, z: pad.z, yaw: pad.rotY, pitch: 0, roll: 0, spin: s?.state === 'boarding' ? 0.18 : 0, phase: 'ground', speed: 0 };
}

/** Is socket `id` aboard any helicopter (boarding or flying)? For hiding peers' bodies. */
export function aboardIds(st = useHeliTour.getState()) {
  const out = new Set();
  for (const p of Object.values(st.pads || {})) if (p?.state !== 'idle') for (const s of p.seats || []) if (s?.id) out.add(s.id);
  return out;
}
